import {
  DEFAULT_LISTING_IMAGE,
  ListingStates,
} from '../../components/exclusives/constants/constants';
import {
  FlipState,
  Listing,
  ListingOOMLS,
  ListingPhoto,
  ListingWithComputedProperties,
  ListingWithDaysRemaining,
  ListingWithListingStates,
  SimilarListing,
} from '../../declarations/exclusives/listing';
import {
  ExclusiveListingState,
  PriorListingPhotoData,
} from '../../declarations/exclusives/exclusiveListingInfo';
import { remainingDaysRoundedToEndTime } from './exclusiveListingInfo';
import isNumber from 'lodash/isNumber';
import { DateTime } from 'luxon';
import { MarketLocation } from '../../declarations/exclusives/market';
import { EXCLUSIVE_RAILS_HOST } from '../../components/globals';

export function getListingStreet(listing: Listing) {
  return listing.address.street ?? '';
}

export function getListingAddressHumanReadable(listing: Listing) {
  return listing.address.human_readable ?? '';
}

// TODO: @exclusives move this field to be computed server side
export function getListingState(listing: Listing | SimilarListing): ListingStates {
  if (
    (!listing.exclusive_listing_info ||
      listing.exclusive_listing_info?.state == ExclusiveListingState.Expired) &&
    listing.flip_state == FlipState.Listed
  ) {
    return ListingStates.OOMLS;
  }

  if (
    (!listing.exclusive_listing_info ||
      listing.exclusive_listing_info.state == ExclusiveListingState.Expired) &&
    [FlipState.ResaleContigenciesRelease, FlipState.ResaleInContract].includes(
      listing.flip_state as FlipState,
    )
  ) {
    return ListingStates.OOMLS_IN_CONTRACT;
  }

  if (listing.exclusive_listing_info.state === ExclusiveListingState.AwaitingSellerCommitment) {
    return ListingStates.EL_AWAITING_SELLER_COMMITMENT;
  }

  if (listing.exclusive_listing_info.state === ExclusiveListingState.Setup) {
    return ListingStates.EL_SETUP;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.InContract) {
    return ListingStates.EL_IN_CONTRACT;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.Sold) {
    return ListingStates.EL_SOLD;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.Expired) {
    return ListingStates.EL_EXPIRED;
  }

  if (
    listing.exclusive_listing_info.reservation_end_time &&
    !listing.exclusive_listing_info.reservation_locked
  ) {
    return ListingStates.RESERVATION_PENDING;
  }

  if (
    listing.exclusive_listing_info.state == 'el_listed' &&
    listing.exclusive_listing_info.reservation_locked !== true
  ) {
    return ListingStates.EL_LISTED;
  }

  if (listing.exclusive_listing_info.reservation_locked === true) {
    return ListingStates.EL_RESERVATION_LOCKED;
  }

  // safe default to allow adding to waitlist
  return ListingStates.EL_SETUP;
}

export function getIsSellerOfferExperiment(listing: Listing) {
  return (
    !getIsOOMLS(getListingWithListingStates(listing)) &&
    Boolean(listing.exclusive_listing_info?.seller_offer_experiment)
  );
}

export function getListingWithListingStates(
  listing: Listing | ListingOOMLS,
): ListingWithListingStates {
  const listingWithELI = {
    ...listing,
    // OOMLS case - we will remove this "translation" once we undo the obfuscation changes
    exclusive_listing_info: getExclusiveListingInfo(listing),
  };
  return {
    ...listingWithELI,
    // TODO: @exclusives move this field to be computed server side
    el_state: getListingState(listingWithELI),
  };
}

export function getExclusiveListingInfo(listing: Listing | ListingOOMLS) {
  if (!listing.exclusive_listing_info) {
    listing.exclusive_listing_info = {
      state: ExclusiveListingState.Expired,
      display_street_name: listing.address.street,
      display_address_human_readable: listing.address.human_readable,
    };
  }
  return listing.exclusive_listing_info;
}

export function getHasOngoingOrUpcomingOpenHouses(listing: Listing) {
  return !!listing.exclusive_listing_info?.ongoing_and_upcoming_open_houses?.length;
}

export function getIsExpiringSoon(listing: Listing) {
  return listing.exclusive_listing_info.end_time
    ? DateTime.fromISO(listing.exclusive_listing_info.end_time) < DateTime.now().plus({ days: 6 })
    : false;
}

export function getIsPostOpenHouse(
  listing: ListingWithComputedProperties,
  hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing),
  expiringSoon = getIsExpiringSoon(listing),
) {
  return (
    listing.el_state === ListingStates.EL_LISTED && !hasOngoingOrUpcomingOpenHouses && expiringSoon
  );
}

export function getIsOpenHouseSchedulingComingSoon(
  listing: ListingWithComputedProperties,
  isPostOpenHouse = getIsPostOpenHouse(listing),
  hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing),
) {
  return !isPostOpenHouse && !hasOngoingOrUpcomingOpenHouses;
}

/** Gets a thumbnail image from listing or fallback to default */
export function getListingThumbnail(listing: Listing, key: keyof ListingPhoto['thumbnail_urls']) {
  if (!listing) return DEFAULT_LISTING_IMAGE;
  return (
    getListingPhotos(listing)?.[0]?.thumbnail_urls?.[key] ||
    listing.exclusive_listing_info?.local_area_map_image ||
    DEFAULT_LISTING_IMAGE
  );
}

/** Map any static image url to a listing photo object */
function mapStaticImageToListingPhoto(url: string): ListingPhoto {
  return {
    url: url,
    thumbnail_urls: {
      _375x250: url,
      _768x512: url,
      _1200x800: url,
      _1800x1200: url,
    },
  };
}

/** Gets listing photos or fallback to defaults */
export function getListingPhotos(listing: Listing): ListingPhoto[] {
  if (listing.listing_photos?.length) {
    return listing.listing_photos;
  }

  if (
    listing.exclusive_listing_info.seller_offer_experiment &&
    listing.exclusive_listing_info.prior_listing_photo_data?.urls.length
  ) {
    return priorListingPhotoDataIntoListingPhotos(
      listing.exclusive_listing_info.prior_listing_photo_data,
    );
  }

  if (listing.exclusive_listing_info?.local_area_map_image) {
    return [mapStaticImageToListingPhoto(listing.exclusive_listing_info.local_area_map_image)];
  }

  return [mapStaticImageToListingPhoto(DEFAULT_LISTING_IMAGE)];
}

export function priorListingPhotoUrlToListingPhoto(url: string): ListingPhoto {
  const base = `${url}?auto=format,compress&ch=Width,DPR&dpr=2`;
  return {
    is_prior: true,
    url: base,
    thumbnail_urls: {
      _375x250: base + '&fit=crop&w=375&h=250',
      _768x512: base + '&fit=crop&w=768&h=512',
      _1200x800: base + '&fit=crop&w=1200&h=800',
      _1800x1200: base + '&fit=crop&w=1800&h=1200',
    },
  };
}

export function priorListingPhotoDataIntoListingPhotos(
  priorListingPhotoData: PriorListingPhotoData | null | undefined,
): ListingPhoto[] {
  if (!priorListingPhotoData) return [];
  return priorListingPhotoData.urls.map(priorListingPhotoUrlToListingPhoto);
}

export function getPriorListingPhotoDataListedAt(listing: Listing) {
  return listing.exclusive_listing_info.prior_listing_photo_data?.listed_at;
}

export function getListingWithRemainingDays<L extends Listing>(
  listing: L,
): ListingWithDaysRemaining & L {
  const daysRemaining = remainingDaysRoundedToEndTime(listing.exclusive_listing_info);
  return { ...listing, ...(isNumber(daysRemaining) && { days_remaining: daysRemaining }) };
}

export function getListingWithComputedProperties(
  listing: Listing | ListingOOMLS,
): ListingWithComputedProperties {
  return getListingWithRemainingDays(getListingWithListingStates(listing));
}

export function getExclusivesEventHomeInfo(listing: Listing) {
  if (getIsOOMLS(getListingWithComputedProperties(listing))) {
    // TODO: We need to support other states (in_contract, sold, etc) in the future, but for now
    // we're only sending actively listed OOMLS to the frontend.
    // TODO: We should also rename home_owner to something like home_type or listing_type
    return {
      home_owner: 'opendoor_owned_mls',
      home_status: 'listed',
    };
  }

  const homeOwner = listing.exclusive_listing_info.seller_offer_experiment
    ? 'customer_owned'
    : 'opendoor_owned';
  let homeStatus = 'available_soon';
  if (listing.exclusive_listing_info.state === 'el_setup') {
    homeStatus = 'available_soon';
  } else if (listing.exclusive_listing_info.state === 'el_listed') {
    homeStatus = 'listed';
  } else if (listing.exclusive_listing_info.state === 'el_in_contract') {
    homeStatus = 'in_contract';
  } else if (listing.exclusive_listing_info.state === 'el_awaiting_seller_commitment') {
    homeStatus = 'unlisted';
  } else if (listing.exclusive_listing_info.state === 'el_sold') {
    homeStatus = 'sold';
  }
  return {
    home_owner: homeOwner,
    home_status: homeStatus,
  };
}

export const DISCOUNT_FOR_SELLER_OWNED_BUYERS_IN_PERCENTAGE = 0.5;

export const OOMLS_DISCOUNT_IN_PERCENTAGE = 2;

export function getListingPriceCents(
  listing: Listing,
  isOOMLS: boolean = getIsOOMLS(getListingWithComputedProperties(listing)),
  isSellerOfferExperiment: boolean = getIsSellerOfferExperiment(listing),
) {
  if (isOOMLS) {
    return (listing.price_cents * (100 - OOMLS_DISCOUNT_IN_PERCENTAGE)) / 100;
  }

  if (isSellerOfferExperiment && getIsExpired(getListingWithComputedProperties(listing))) {
    // When listing.price_cents does not exist, it gets serialized to 0
    return 0;
  }
  return listing.exclusive_listing_info?.exclusives_listing_price_cents || listing.price_cents;
}

export function getOriginalListingPriceCents(
  listing: Listing,
  isOOMLS: boolean = getIsOOMLS(getListingWithComputedProperties(listing)),
  isSellerOfferExperiment: boolean = getIsSellerOfferExperiment(listing),
) {
  if (isOOMLS) {
    return listing.price_cents;
  }
  if (
    listing.exclusive_listing_info.mls_listing_price_cents ===
    listing.exclusive_listing_info.exclusives_listing_price_cents
  ) {
    return null;
  }
  if (isSellerOfferExperiment && getIsExpired(getListingWithComputedProperties(listing))) {
    return null;
  }
  return listing.exclusive_listing_info.mls_listing_price_cents;
}

const TOURABLE_LISTING_STATES = [
  ListingStates.RESERVATION_PENDING,
  ListingStates.EL_LISTED,
  ListingStates.EL_IN_CONTRACT,
];

export function getIsListingTourable(listing: ListingWithComputedProperties) {
  return !getIsSellerOfferExperiment(listing) && TOURABLE_LISTING_STATES.includes(listing.el_state);
}

export function getIsOOMLS(listing: ListingWithComputedProperties) {
  return [ListingStates.OOMLS, ListingStates.OOMLS_IN_CONTRACT].includes(listing.el_state);
}

export function getIsInContract(listing: ListingWithComputedProperties) {
  return [ListingStates.OOMLS_IN_CONTRACT, ListingStates.EL_IN_CONTRACT].includes(listing.el_state);
}

export function getIsAwaitingSellerCommitment(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_AWAITING_SELLER_COMMITMENT;
}

export function getIsExpired(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_EXPIRED;
}

export function getIsAvailableSoon(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_SETUP;
}

export function getBallparkPriceRange(listing: Listing) {
  return listing.exclusive_listing_info.ballpark_price_range;
}

const ACTIVE_ELI_STATES: Listing['exclusive_listing_info']['state'][] = [
  ExclusiveListingState.Listed,
  ExclusiveListingState.Setup,
];

export function getActiveListings(listings: ListingWithComputedProperties[]) {
  return listings.filter(
    (l) =>
      l.el_state == ListingStates.OOMLS ||
      ACTIVE_ELI_STATES.includes(l.exclusive_listing_info.state),
  );
}

export class FetchListingError extends Error {
  constructor(readonly response: Response, readonly error?: unknown) {
    super(`FeatchListingError: response status Code ${response.status}`);
  }
}

export async function fetchListings<Bool extends boolean>(
  options: { market?: MarketLocation; includeOomls?: Bool } = {},
): Promise<Bool extends true ? ListingOOMLS[] : Listing[]> {
  const urlSearchParams = new URLSearchParams();
  if (options.market) {
    urlSearchParams.set('market', options.market);
  }
  if (options.includeOomls) {
    urlSearchParams.set('include_oomls', 'true');
  }
  const response = await fetch(
    `${EXCLUSIVE_RAILS_HOST}/api/v1/exclusive_listings?${urlSearchParams}`,
  );
  if (!response.ok) {
    throw new FetchListingError(response);
  }
  try {
    const body: Bool extends true ? ListingOOMLS[] : Listing[] = await response.json();
    if (!Array.isArray(body)) {
      throw new FetchListingError(response, body);
    }
    return body;
  } catch (e) {
    throw new FetchListingError(response, e);
  }
}
